import { Drawer, Grid, Group, NumberInput, Select, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import type { UseFormReturnType } from '@mantine/form';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys } from 'locales/index';
import { useTranslation } from 'locales/logic';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import { useGetConstants } from 'shared/utils/selectors';

export type ThirdPartyCaseFilter = {
  filenumber?: string;
  debtor_name?: string;
  invoice_reference?: string;
  status?: string;
  unread_messages?: string;
  case_type?: string;
  account_manager_id?: string;
  total_tvac?: number | string;
  total_tvac_operator?: string;
  remaining_balance?: number | string;
  remaining_balance_operator?: string;
  sent_date_after?: string | null;
  sent_date_before?: string | null;
  update_date_after?: string | null;
  update_date_before?: string | null;
};

type Props = {
  isOpen: boolean;
  close: () => void;
  form: UseFormReturnType<ThirdPartyCaseFilter>;
  isClosedTab: boolean;
  isAllTab: boolean;
};

export const ThirdPartyCaseFilterDrawer = ({
  isOpen,
  close,
  form,
  isClosedTab,
  isAllTab,
}: Props) => {
  const { t } = useTranslation();
  const profile = useProfile();
  const constants = useGetConstants();

  const canFilterByAccountManager = profile.isAdmin || profile.roles.includes('all_invoices');
  const { users = [] } = useLoadLightUsers(
    false,
    { query: '' },
    { isEnabled: canFilterByAccountManager },
  );

  const toSelectItems = (items: Array<{ value: string; description: string }>) =>
    items.map(item => ({ value: item.value, label: item.description }));

  const operatorItems = toSelectItems(constants.operators);
  const statusItems = toSelectItems(
    isAllTab
      ? constants.tp_case_statuses
      : constants.tp_case_statuses.filter(status => status.value !== 'closed'),
  );

  return (
    <Drawer position="right" opened={isOpen} onClose={close} title={t(i18nKeys.FILTER)}>
      <Grid>
        <Grid.Col span={12}>
          <TextInput
            label={t(i18nKeys.THIRD_PARTY_CASES.REFERENCE)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.REFERENCE)}
            {...form.getInputProps('filenumber')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t(i18nKeys.COMMON.CLIENT)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.CLIENT)}
            {...form.getInputProps('debtor_name')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <TextInput
            label={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.INVOICE_NUMBER)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.INVOICE_NUMBER)}
            {...form.getInputProps('invoice_reference')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <Select
            label={t(i18nKeys.CASE_TYPE)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.CASE_TYPE)}
            data={toSelectItems(constants.tp_case_types)}
            clearable
            searchable
            {...form.getInputProps('case_type')}
          />
        </Grid.Col>
        {!isClosedTab && (
          <Grid.Col span={6}>
            <Select
              label={t(i18nKeys.STATUS)}
              placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.STATUS)}
              data={statusItems}
              clearable
              {...form.getInputProps('status')}
            />
          </Grid.Col>
        )}
        <Grid.Col span={6}>
          <Select
            label={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.UNREAD_MESSAGES)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.UNREAD_MESSAGES)}
            data={[
              { value: 'true', label: t(i18nKeys.YES) },
              { value: 'false', label: t(i18nKeys.NO) },
            ]}
            clearable
            {...form.getInputProps('unread_messages')}
          />
        </Grid.Col>
        {canFilterByAccountManager && (
          <Grid.Col span={12}>
            <Select
              label={t(i18nKeys.ACCOUNT_MANAGER)}
              placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.ACCOUNT_MANAGER)}
              data={users.map(user => ({ value: String(user.id), label: user.name.full }))}
              clearable
              searchable
              {...form.getInputProps('account_manager_id')}
            />
          </Grid.Col>
        )}
        <Grid.Col span={12}>
          <AmountFilterField
            label={t(i18nKeys.TOTAL_VAT_PRICE)}
            operatorPlaceholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.OPERATOR)}
            valuePlaceholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.AMOUNT)}
            operatorItems={operatorItems}
            valueProps={form.getInputProps('total_tvac')}
            operatorProps={form.getInputProps('total_tvac_operator')}
          />
        </Grid.Col>
        <Grid.Col span={12}>
          <AmountFilterField
            label={t(i18nKeys.BALANCE)}
            operatorPlaceholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.OPERATOR)}
            valuePlaceholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.AMOUNT)}
            operatorItems={operatorItems}
            valueProps={form.getInputProps('remaining_balance')}
            operatorProps={form.getInputProps('remaining_balance_operator')}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput
            label={t(i18nKeys.CREATION_DATE)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.DATE)}
            type="range"
            allowSingleDateInRange
            clearable
            value={[form.values.sent_date_after ?? null, form.values.sent_date_before ?? null]}
            onChange={([after, before]) => {
              form.setFieldValue('sent_date_after', after);
              form.setFieldValue('sent_date_before', before);
            }}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <DatePickerInput
            label={t(i18nKeys.LAST_UPDATE)}
            placeholder={t(i18nKeys.THIRD_PARTY_CASES.FILTERS.PLACEHOLDERS.DATE)}
            type="range"
            allowSingleDateInRange
            clearable
            value={[form.values.update_date_after ?? null, form.values.update_date_before ?? null]}
            onChange={([after, before]) => {
              form.setFieldValue('update_date_after', after);
              form.setFieldValue('update_date_before', before);
            }}
          />
        </Grid.Col>
      </Grid>
    </Drawer>
  );
};

type AmountFilterFieldProps = {
  label: string;
  operatorPlaceholder: string;
  valuePlaceholder: string;
  operatorItems: Array<{ value: string; label: string }>;
  valueProps: ReturnType<UseFormReturnType<ThirdPartyCaseFilter>['getInputProps']>;
  operatorProps: ReturnType<UseFormReturnType<ThirdPartyCaseFilter>['getInputProps']>;
};

const AmountFilterField = ({
  label,
  operatorPlaceholder,
  valuePlaceholder,
  operatorItems,
  valueProps,
  operatorProps,
}: AmountFilterFieldProps) => (
  <Group grow gap="xs" align="flex-end">
    <Select
      label={label}
      placeholder={operatorPlaceholder}
      data={operatorItems}
      clearable
      {...operatorProps}
    />
    <NumberInput placeholder={valuePlaceholder} min={0} decimalScale={2} {...valueProps} />
  </Group>
);
